import * as Sentry from "@sentry/react";
import type {
	AdapterStatus,
	AudioPlaybackService,
	PlaybackStatus,
	SingleAudioPlaybackServiceArgs,
} from "../../application/ports";
import type { PlaybackStore } from "../../application/stores/playbackStore";
import { AudioContextAdapter } from "./AudioContextAdapter";
import type { BaseAudioModel } from "@coffitivity/shared";

export class StreamingAudioContextAdapter implements AudioPlaybackService {
	// public properties
	public data: BaseAudioModel;
	public preferredMimeType: "mp3" | "ogg";
	public usesAudioContext = true;
	public isSubTrack: boolean;
	public onPlay: (() => void) | null = null;
	public onPause: (() => void) | null = null;
	public onEnded: (() => void) | null = null;

	// private properties
	private store: PlaybackStore;
	private _url: string;
	private _isAudioContextInitialized = false;
	private audioContext: AudioContext | undefined = undefined;
	private sourceGainNode: GainNode | undefined = undefined;
	private audio: HTMLAudioElement;
	private sourceNode: MediaElementAudioSourceNode | undefined = undefined;
	private fadeDuration: number;
	private isLooping: boolean;
	private _isPlaying = false;
	private _trackVolume: number;
	private updateAdapterState: (
		trackId: string,
		playbackStatus?: PlaybackStatus,
		volume?: number,
	) => void;

	constructor(_args: SingleAudioPlaybackServiceArgs) {
		this.data = _args.track;
		this.store = _args.store;

		const initialState = this.store.getState();

		this.preferredMimeType = initialState.preferredMimeType;
		this.isLooping = this.data.isLooping;
		this.isSubTrack = _args.isSubTrack ?? false;
		this.updateAdapterState = _args.updateAdapterState ?? (() => {});
		this._trackVolume = _args.trackVolume ?? 1;
		this.fadeDuration = 0.3;
		this._url = `https://audios.coffitivity.com/${this.data.slug}.${this.preferredMimeType}`;

		this.audio = new Audio();
		this.audio.crossOrigin = "anonymous";
		this.audio.preload = "auto";
		this.audio.loop = this.isLooping;

		this.audio.onplay = () => {
			this.onPlay?.();
		};
		this.audio.onpause = () => {
			this.onPause?.();
		};
		this.audio.onended = () => {
			this._isPlaying = false;
			if (!this.isSubTrack) {
				// notify the global state that the track has ended
				this.store.getState().setCurrentTrackStatus("ENDED");
			} else {
				this.updateAdapterState(this.data.id, "ENDED");
			}
			this.onEnded?.();
		};

		this.audio.onerror = () => {
			this._isPlaying = false;
			const error = this.audio.error!;
			if (!this.isSubTrack) {
				// notify the global state that the track is paused
				this.store.getState().setCurrentTrackStatus("ERROR");
			} else {
				this.updateAdapterState(this.data.id, "ERROR");
			}

			const errorMap = {
				1: "MEDIA_ERR_ABORTED",
				2: "MEDIA_ERR_NETWORK",
				3: "MEDIA_ERR_DECODE",
				4: "MEDIA_ERR_SRC_NOT_SUPPORTED",
			};
			const message =
				errorMap[error.code as keyof typeof errorMap] || "UNKNOWN_MEDIA_ERROR";
			console.error(`[Audio Error] ${message} (${error.code})`, error);
			Sentry.captureMessage(`Audio playback error: ${message}`, {
				level: "error",
				extra: {
					code: error.code,
					currentSrc: this.audio.currentSrc,
				},
			});
		};
	}

	private async fadeGainTo(target: number, duration: number): Promise<void> {
		const now = this.audioContext!.currentTime;
		const gain = this.sourceGainNode!.gain;
		gain.cancelScheduledValues(now);
		gain.setValueAtTime(gain.value, now);
		gain.linearRampToValueAtTime(target, now + duration);

		return new Promise((resolve) => {
			setTimeout(() => {
				resolve();
			}, duration * 1000);
		});
	}

	private initializeAudioContext(): void {
		if (this._isAudioContextInitialized) return;

		// setup the audio context connections
		this.audioContext = AudioContextAdapter.getInstance().getAudioContext();
		this.sourceGainNode = this.audioContext.createGain();
		this.sourceGainNode.gain.value = this._trackVolume;
		this.sourceGainNode.connect(
			// we already set the master volume above, just connect the node to the master gain
			AudioContextAdapter.getInstance().getMasterGain(),
		);
		this.sourceNode = this.audioContext.createMediaElementSource(this.audio);
		this.sourceNode.connect(this.sourceGainNode);

		this._isAudioContextInitialized = true;
	}

	async load(): Promise<void> {
		this.audio.src = this._url;
		this.audio.load();
	}

	async play(): Promise<void> {
		try {
			// initialize the audio context if it hasn't been already
			this.initializeAudioContext();

			// Update the state so user see a quick updates
			// Also! Only set this as the current track if this is not a sub-track
			// the blended adapter will handle setting the current track otherwise
			if (!this.isSubTrack) {
				// not a sub track - set as the current track
				if (this.store.getState().currentTrack?.id !== this.data.id) {
					this.store.getState().setCurrentTrack(this.data);
				}
			}

			// now try and play the track
			await this.audioContext!.resume();
			await this.audio.play();
			this.fadeGainTo(this._trackVolume, this.fadeDuration);

			// update the internal state status to playing
			this._isPlaying = true;

			if (!this.isSubTrack) {
				// notify the global state that the track is paused
				this.store.getState().setCurrentTrackStatus("PLAYING");
			} else {
				this.updateAdapterState(this.data.id, "PLAYING");
			}
		} catch (error) {
			Sentry.captureException(error, {
				tags: {
					audioSource: this.audio.src,
					audioContextState: this.audioContext?.state,
				},
			});
			return Promise.reject(error);
		}
	}

	async pause(): Promise<void> {
		try {
			if (!this._isPlaying) return;

			// Send the status change event before the async fade out
			// update the internal state status to paused
			this._isPlaying = false;

			if (!this.isSubTrack) {
				// notify the global state that the track is paused
				this.store.getState().setCurrentTrackStatus("PAUSED");
			} else {
				this.updateAdapterState(this.data.id, "PAUSED");
			}

			// pause the audio
			// we do this last because the pause event isn't instantaneous and we want the rest of
			// the code to react immediately to this event
			await this.fadeGainTo(0, this.fadeDuration);
			this.audio.pause();
		} catch (error) {
			Sentry.captureException(error, {
				tags: {
					audioSource: this.audio.src,
				},
			});
			return Promise.reject(error);
		}
	}

	async stop(): Promise<void> {
		await this.fadeGainTo(0, this.fadeDuration);
		this.audio.pause();
		this.audio.currentTime = 0;
	}

	setLoop(loop: boolean): void {
		this.isLooping = loop;
		this.audio.loop = loop;
	}

	setTrackVolume(volume: number): void {
		// we to initialize the audio context before setting the volume
		this.initializeAudioContext();

		// only set this if the track is a sub-track
		if (this.isSubTrack) {
			this._trackVolume = volume;
			this.sourceGainNode!.gain.setValueAtTime(
				volume,
				this.audioContext!.currentTime,
			);
			this.updateAdapterState(this.data.id, undefined, this._trackVolume);
		}
	}

	isPlaying(): boolean {
		return this._isPlaying;
	}

	cleanup(): void {
		try {
			this.sourceNode?.disconnect();
		} catch {}
		this.audio.src = "";
		this.audio.load();
	}

	status(): AdapterStatus {
		return {
			trackId: this.data.id,
			status: this._isPlaying ? "PLAYING" : "PAUSED",
			error: this.audio.error,
			trackVolume: this._trackVolume,
			blendStatus: [],
		};
	}
}
